import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, IconButton, Tooltip, Typography, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { UnifiedAlertsIcons } from './icons';
import PageWrapper from 'components/Common/PageWraper';
import SeverityTiles, { TILES_CONFIG } from './SeverityTiles/SeverityTiles';
import AlertsTable from './AlertsTable/AlertsTable';
import TrendBySector from './TrendCharts/TrendBySector';
import TrendByProgram from './TrendCharts/TrendByProgram';
import SourcesPieChart from './TrendCharts/SourcesPieChart';
import MostImpactedApp from './TrendCharts/MostImpactedApp';
import AdvancedFilterDrawer from './AdvancedFilterDrawer/AdvancedFilterDrawer';
import html2canvas from 'html2canvas'
import { useUnifiedAlerts } from './hooks/useUnifiedAlerts';
import { TIME_RANGE_OPTIONS } from './AdvancedFilterDrawer';
import ImagePreview from 'utils/ImagePreview';
import CaptureExportControls from 'utils/CaptureExportControls';
import { generateDashboardPdf } from 'utils/pdfExportUtil';
import type { PdfColumn } from 'utils/pdfExportUtil';
import ManageSopDialog, { ManageSopEntityConfig } from './ManageSOP/ManageSOPDialog';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import { snowUrl } from 'service/config';
import * as unifiedAlertsService from 'service/unifiedAlerts';
import { useHeader } from 'Context/contexts/HeaderTabRightContentContext';
import AdvancedFilterPanel from './AdvancedFilterDrawer/AdvancedFilterPanel';
import AlertsProvider from './AlertsProvider';
import AlertsTableWrapper, { flattenAlertItem } from './AlertsTableWrapper';
import IdleFilterIconBlue from '../../assets/filter_idel_blue.svg';
import AlertsOverview from './AlertsOverview';
import AlertsTablePage from './AlertsTablePage';
import { useSearchParams } from 'react-router-dom';
import CustomSwitch from 'components/Button/CustomSwitch';
import moment from 'moment-timezone';
import { TIMEZONE_MAP } from 'utils/common-util';
import { ExportIcon } from 'components/CommonIcons/Icons';
import ExportLimitWarningDialog from '../Modal/ExportLimitWarningDialog';

const CHART_CARDS = [
  { key: 0, label: 'Trends by Sector',         sub: 'Last 30 Days', Icon: UnifiedAlertsIcons.TrendBySectorIcon },
  { key: 1, label: 'Trends by Program',         sub: 'All Markets',  Icon: UnifiedAlertsIcons.TrendByProgramIcon },
  { key: 2, label: 'Sources',                   sub: '33 critical',  Icon: UnifiedAlertsIcons.SourcesIcon },
  { key: 3, label: 'Most Impacted Applications', sub: 'View All',     Icon: UnifiedAlertsIcons.MostImpactedAppIcon },
  { key: 4, label: 'All Open Alerts By Severity', sub: 'View All',     Icon: UnifiedAlertsIcons.OpenAlertsBySeverity },
];

const includeGroupByMenuColumns = [
  'assignment_group',
  'program',
  'sector',
  'domain',
  'source',
  'department'
];

const groupByList = [
  { label: 'Assignment Group', value: 'assignment_group' },
  { label: 'Program', value: 'program' },
  { label: 'Sector', value: 'sector' },
  { label: 'Domain', value: 'domain' },
  { label: 'Source', value: 'source' },
  { label: 'Department', value: 'department' },
];

  export const timestamp = new Date();
  export const formattedDate = timestamp.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).replace(/[/, ]/g, '_'); 

  type Screen =
  | { type: 'overview' }
  | { type: 'table' };

const OPEN_TILE_STATUSES = ['Open', 'Reopen'];

const SnowUnifiedAlertsDashboard = () => {
  const theme = useTheme();
  const p = theme.palette;
  const { setRightContextContent } = useHeader();

  const [activeBottomTab, setActiveBottomTab] = useState(0);
  const [activeContent, setActiveContent] = useState<'table' | 'chart'>('table');
  const [hoveredBottomTab, setHoveredBottomTab] = useState<number | null>(null);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false); 
  const [screenCapturing, setScreenCapturing] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [openPreview, setOpenPreview] = useState(false)
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [groupedData, setGroupedData] = useState<any[]>([]);
  const [isManageSopOpen, setIsManageSopOpen] = useState(false);
  const [isExportWarningOpen, setIsExportWarningOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [screen, setScreen] = useState<Screen>({
    type: 'overview',
  });

  const {
    activeTile,
    setActiveTile,
    page,
    pageSize,
    sortingInfo,
    appliedFilters,
    summary,
    alerts,
    isDataOpsAdmin,
    totalAlerts,
    trendSector,
    trendProgram,
    sources,
    mostImpactedApps,
    severityData,
    isLoading,
    isChartsLoading,
    isExporting,
    filterOptions,
    setPage,
    setPageSize,
    setSortingInfo,
    handleTileClick,
    handleApplyFilters,
    handleSaveDefaultFilters,
    handleResetDefaultFilters,
    handleExport,
    fetchPdfData,
    setGroupBy,
    groupBy,
    setIsGrouped,
    loadMoreChildren,
    loadingGroupId,
    setLoadingGroupId,
    summaryLoading,
    filterRelationshipRecords,
    alertCountView,
    setAlertCountView,
    columnFilters,
    handleColumnFilterChange,
    getColumnFilterValues
  } = useUnifiedAlerts();

  const handleExportClick = () => {
    const exportLimit = filterOptions?.exportLimit || 200000;
    if (totalAlerts > exportLimit) {
      setIsExportWarningOpen(true);
    } else {
      handleExport();
    }
  };

  const getSopFilterOptions = useCallback((value: any): string[] => {
    if (!value) return [];

    if (Array.isArray(value)) {
      return Array.from(
        new Set(
          value
            .map((item) => {
              if (typeof item === 'string') return item;
              return item?.label || item?.value || '';
            })
            .filter(Boolean)
        )
      );
    }

    if (typeof value === 'object') {
      const values = Object.entries(value).flatMap(([key, val]) => {
        if (Array.isArray(val)) return [key, ...val];
        if (typeof val === 'string') return [key, val];
        return [key];
      });

      return Array.from(new Set(values.map((item) => String(item)).filter(Boolean)));
    }

    return [String(value)];
  }, []);

  const getSopFilterValue = useCallback((value: any) => {
    if (!value) return undefined;

    if (Array.isArray(value)) {
      const values = value
        .map((item) => {
          if (typeof item === 'string') return item;
          return item?.value || item?.label || '';
        })
        .filter(Boolean);

      return values.length ? values.join(',') : undefined;
    }

    if (typeof value === 'object') {
      const objectValue = value?.value || value?.label;
      return objectValue ? String(objectValue) : undefined;
    }

    return String(value);
  }, []);

  const getAlertSopColumns = useCallback(
    ({
      selectedIds,
      setSelectedIds,
      colors,
      autoColumnWidth,
      snowUrl,
    }: any) => {
      return [
        {
          key: 'selection-checkbox',
          name: 'HeaderCheckboxMapping',
          width: '40px',
          frozen: true,
          formatter: ({ row }: { row: any }) => {
            const alertId = row?.number;
            const isSelected = selectedIds.includes(alertId);

            return (
              <Checkbox
                size="small"
                checked={isSelected}
                onChange={() => {
                  setSelectedIds((prev: string[]) =>
                    isSelected
                      ? prev.filter((selectedId) => selectedId !== alertId)
                      : [...prev, alertId]
                  );
                }}
                sx={{ color: '#9CA3AF', '&.Mui-checked': { color: '#39ace7' }, p: 0 }}
              />
            );
          },
        },
        {
          key: 'sopActions',
          name: 'SOP ACTIONS',
          width: 110,
          formatter: ({ row }: { row: any }) => {
            const isSopLinked = row?.sopId || row?.hasSOPLinked || row?.hasSopLinked;

            return (
              <Tooltip title={isSopLinked ? 'SOP Linked' : 'SOP Not Linked'} arrow>
                <IconButton
                  size="small"
                  sx={{
                    color: isSopLinked
                      ? colors.buttons.primary.color
                      : colors.buttons.primary.disabledBg,
                    p: 0.5,
                    cursor: isSopLinked ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (row?.sopLink) {
                      window.open(row.sopLink, '_blank');
                    }
                  }}
                >
                  <DescriptionRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            );
          },
        },
        {
          key: 'number',
          name: 'ALERT ID',
          width: autoColumnWidth(150),
          formatter: ({ row }: { row: any }) => (
              <Typography
                variant="tableRowFont"
                sx={{
                  color: 'blue',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => window.open(`${snowUrl}/now/sow/record/em_alert/${row.sys_id}`, '_blank')}
              >
                {row.number}
              </Typography>
          ),
        },
        { key: 'sopName', name: 'SOP NAME', width: autoColumnWidth(150) },
        { key: 'description', name: 'DESCRIPTION', width: autoColumnWidth(220, 2) },
        { key: 'source', name: 'SOURCE', width: autoColumnWidth(130) },
        { key: 'severity', name: 'SEVERITY', width: autoColumnWidth(110) },
        { key: 'sn_priority_group', name: 'PRIORITY', width: autoColumnWidth(110) },
        { key: 'state', name: 'STATUS', width: autoColumnWidth(110) },
        { key: 'age', name: 'AGE', width: autoColumnWidth(120) },
        { key: 'incident', name: 'INCIDENT', width: autoColumnWidth(120) },
        { key: 'assignment_group', name: 'ASSIGNMENT GROUP', width: autoColumnWidth(180) },
        { key: 'last_event_time', name: 'LAST EVENT TIME', width: autoColumnWidth(170) },
        { key: 'category', name: 'CATEGORY', width: autoColumnWidth(140) },
        { key: 'domain', name: 'DOMAIN', width: autoColumnWidth(120) },
        { key: 'classification', name: 'CLASSIFICATION', width: autoColumnWidth(160) },
        { key: 'cmdb_ci', name: 'CI', width: autoColumnWidth(140) },
        { key: 'sys_created_by', name: 'CREATED BY', width: autoColumnWidth(150) },
        { key: 'sys_updated_by', name: 'UPDATED BY', width: autoColumnWidth(150) },
        { key: 'sector', name: 'SECTOR', width: autoColumnWidth(130) },
        { key: 'application', name: 'APPLICATION', width: autoColumnWidth(150) },
        { key: 'businessUnit', name: 'BUSINESS UNIT', width: autoColumnWidth(150) },
        { key: 'capability', name: 'CAPABILITY', width: autoColumnWidth(150) },
        { key: 'department', name: 'DEPARTMENT', width: autoColumnWidth(150) },
        { key: 'program', name: 'PROGRAM', width: autoColumnWidth(150) },
      ];
    },
    []
  );

  const fetchAlertSopRows = useCallback(
    async ({ filters, page, pageSize, sortingInfo, activeTile, searchCursor, direction }: any) => {
      const domain = getSopFilterValue(filters?.Domains || filters?.domain || filters?.domains);
      const program = getSopFilterValue(filters?.Programs || filters?.program || filters?.programs);
      const sector = getSopFilterValue(filters?.Sector || filters?.sector);
      const alertPatterns = getSopFilterValue(filters?.['Alert Pattern'] || filters?.alertPatterns);

      const params: any = {
        page,
        pageSize,
        ...(sortingInfo?.sortBy && {
          sortBy: sortingInfo.sortBy,
          sortDir: sortingInfo.sortDir,
        }),
        ...(domain && {
          domain,
          domains: domain,
        }),
        ...(program && {
          program,
          programs: program,
        }),
        ...(sector && { sector }),
        ...(alertPatterns && { alertPatterns }),
        ...(searchCursor !== undefined && { searchCursor }),
        ...(direction && { direction }),
      };

      if (activeTile) {
        if (activeTile !== 'Closed') {
          params.severity = activeTile;
          params.status = 'Open, Reopen';
        } else {
          params.status = 'Closed';
        }
      }

      const response: any = await unifiedAlertsService.getAlerts(params);
      const responseData = response?.data ?? response;
      const rows = Array.isArray(responseData)
        ? responseData
        : responseData?.data?.docs ?? responseData?.docs ?? responseData?.data ?? [];

      return {
        rows: Array.isArray(rows) ? rows : [],
        total:
          response?.pagination?.total ??
          responseData?.pagination?.total ??
          responseData?.total ??
          responseData?.totalDocs ??
          (Array.isArray(rows) ? rows.length : 0),
        pagination: response?.pagination ?? responseData?.pagination,
      };
    },
    [getSopFilterValue]
  );

  const alertSopEntityConfig: ManageSopEntityConfig = useMemo(
    () => ({
      title: 'Alerts',
      stepOneLabel: 'Select Alerts',
      selectedItemsLabel: 'Selected Alert IDs',
      tableKey: 'manage-sop-alerts-grid',
      tableType: 'ManageSopAlerts',
      defaultColumnNames: [
        'HeaderCheckboxMapping',
        'SOP ACTIONS',
        'ALERT ID',
        'SOP NAME',
        'DESCRIPTION',
        'SOURCE',
        'SEVERITY',
        'PRIORITY',
        'STATUS',
        'AGE',
      ],
      initialFilters: {
        Domains: [],
        Programs: [],
        Sector: [],
        'Alert Pattern': [],
      },
      filterOptions: {
        Domains: getSopFilterOptions(filterOptions?.domains || filterOptions?.domain || filterOptions?.domainDropdownValues),
        Programs: getSopFilterOptions(filterOptions?.programs || filterOptions?.program || filterOptions?.programDropdownValues),
        Sector: getSopFilterOptions(filterOptions?.sector || filterOptions?.sectorDropdownValues),
        'Alert Pattern': [],
      },
      disabledKeys: ['Alert Pattern'],
      defaultSorting: { sortBy: 'last_event_time', sortDir: 'DESC' },
      getRowId: (row: any) => row?.number,
      getRowLabel: (row: any) => row?.number,
      getColumns: getAlertSopColumns,
      fetchRows: fetchAlertSopRows,
      normalizeFilters: (filters: any) => ({
        Domains: filters?.Domains || [],
        Programs: filters?.Programs || [],
        Sector: filters?.Sector || [],
        'Alert Pattern': filters?.['Alert Pattern'] || [],
      }),
    }),
    [filterOptions, getSopFilterOptions, getAlertSopColumns, fetchAlertSopRows]
  );

  const getGroupValue = useCallback((row: any, groupByValue: string) => {
    return row?.[groupByValue] ?? row?.groupValue ?? row?.key ?? row?.value ?? row?.name ?? 'Unassigned';
  }, []);

  const getGroupCount = useCallback((row: any, groupByValue: string) => {
    const countField = `${groupByValue}Count`;
    return row?.[countField] ?? row?.totalChildrenCount ?? row?.totalChildren ?? row?.totalCount ?? row?.unique_alerts_count ?? row?.count ?? row?.children?.length ?? 0;
  }, []);

  const handleGroupToggle = useCallback((row: any) => {
    if (!groupBy || groupBy === 'none') return;

    setGroupedData(prevData => {
      // Find the index of the parent row
      const rowGroupValue = getGroupValue(row, groupBy);
      const parentIndex = prevData.findIndex((r: any) =>
        r.isParent === true && getGroupValue(r, groupBy) === rowGroupValue
      );

      if (parentIndex === -1) return prevData;

      const currentParent = prevData[parentIndex];

      // IMPORTANT: Get the actual total from the count field
      const countField = `${groupBy}Count`;
      const actualTotalChildren = currentParent[countField] || currentParent.totalChildrenCount || currentParent.totalChildren || currentParent.children?.length || 0;

      const initialShowCount = 50;
      const hasMoreChildren = actualTotalChildren > initialShowCount;

      // Create a new array
      const newRows = [...prevData];

      const parentGroupValue = getGroupValue(currentParent, groupBy);
      if (!currentParent.isExpanded) {
        // EXPAND: Insert children after the parent
        const childrenToShow = (currentParent.children || []).slice(0, initialShowCount);
        const childrenWithFlag = childrenToShow.map((child: any, idx: number) => ({
          ...child,
          [groupBy]: parentGroupValue,
          isChildren: true,
          childIndex: idx,
          parentGroupValue: parentGroupValue
        }));

        // Calculate insert position (right after the parent)
        const insertPosition = parentIndex + 1;

        // Insert children
        newRows.splice(insertPosition, 0, ...childrenWithFlag);

        if (hasMoreChildren && childrenToShow.length < actualTotalChildren) {
          const showMoreRow = {
            id: `show-more-${getGroupValue(currentParent, groupBy)}`,
            isShowMoreRow: true,
            parentRow: {
              ...currentParent,
              totalChildrenCount: actualTotalChildren // ← CRITICAL: Update parentRow with actual total
            },
            currentShowCount: childrenToShow.length,
            totalChildrenCount: actualTotalChildren, // ← Use actual total here
            [groupBy]: getGroupValue(currentParent, groupBy),
            groupBy: groupBy
          };
          newRows.splice(insertPosition + childrenToShow.length, 0, showMoreRow);
        }

        // Update parent state
        newRows[parentIndex] = {
          ...currentParent,
          isExpanded: true,
          showChildrenCount: childrenToShow.length,
          totalChildrenCount: actualTotalChildren // ← Also update parent's totalChildrenCount
        };
      } else {
        // COLLAPSE: Remove only the children and show more rows of this specific parent
        const showChildrenCount = currentParent.showChildrenCount || initialShowCount;
        let rowsToRemove = Math.min(showChildrenCount, actualTotalChildren);

        // Check if there's a show more row
        const hasShowMoreRow = hasMoreChildren && showChildrenCount < actualTotalChildren;
        if (hasShowMoreRow) {
          rowsToRemove += 1;
        }

        // Remove rows starting from position after parent
        newRows.splice(parentIndex + 1, rowsToRemove);

        // Update parent state
        newRows[parentIndex] = {
          ...currentParent,
          isExpanded: false,
          showChildrenCount: initialShowCount
        };
      }

      return newRows;
    });
  }, [groupBy, getGroupValue]);

  const handleShowMoreClick = useCallback(async (showMoreRow: any) => {
    const { parentRow, currentShowCount, groupBy: groupByValue } = showMoreRow;

    // Use the groupBy from the showMoreRow or fallback to the parentRow's groupBy property
    const actualGroupByValue = groupByValue || groupBy;
    const groupValue = getGroupValue(parentRow, actualGroupByValue);

    // Get the actual total from the count field
    const countField = `${actualGroupByValue}Count`;
    const actualTotalChildrenCount = parentRow[countField] || parentRow.totalChildrenCount || parentRow.totalChildren;

    if (!actualGroupByValue || actualGroupByValue === 'none') {
      console.error('Invalid groupBy value:', actualGroupByValue);
      return;
    }

    if (groupValue === undefined || groupValue === null || groupValue === '') {
      console.error('Invalid group value:', groupValue);
      return;
    }

    // Check if we have all children already loaded
    if (currentShowCount >= actualTotalChildrenCount) {
      console.log("All children already loaded");
      return;
    }

    // Set loading state for this specific group
    setLoadingGroupId(`${actualGroupByValue}-${groupValue}`);

    try {
      // Load next batch from API
      const result = await loadMoreChildren(actualGroupByValue, groupValue, currentShowCount, parentRow);

      if (result && result.children && result.children.length > 0) {
        setGroupedData(prevData => {
          const parentIndex = prevData.findIndex((r: any) =>
            getGroupValue(r, actualGroupByValue) === groupValue && r.isParent === true
          );

          if (parentIndex === -1) return prevData;

          const currentParent = prevData[parentIndex];

          // Use the actual total from the parent
          const totalCount = currentParent[countField] || currentParent.totalChildrenCount || currentParent.totalChildren;

          // Find and remove the current "Show More" row
          const showMoreIndex = prevData.findIndex((r: any) =>
            r.id === showMoreRow.id
          );

          if (showMoreIndex === -1) return prevData;

          const newRows = [...prevData];

          const visibleChildKeys = new Set(
            prevData
              .filter((r: any) => r?.isChildren === true && r?.parentGroupValue === groupValue)
              .map((r: any) => r?.sys_id || r?.number || r?.alert_id || r?.id)
              .filter(Boolean)
          );

          const uniqueChildren = result.children.filter((child: any) => {
            const childKey = child?.sys_id || child?.number || child?.alert_id || child?.id;
            return childKey ? !visibleChildKeys.has(childKey) : true;
          });

          if (!uniqueChildren.length) {
            return prevData;
          }

          // Calculate new show count
          const newShowCount = Math.min(currentShowCount + uniqueChildren.length, totalCount);

          // Check if there are more children to load
          const hasMoreChildren = result.hasMore ?? newShowCount < totalCount;

          const childrenWithFlag = uniqueChildren.map((child: any, idx: number) => ({
            ...child,
            isChildren: true,
            childIndex: currentShowCount + idx,
            parentGroupValue: groupValue,
          }));

          // Find the last visible child row for this parent group
          const visibleChildIndexes = prevData
            .map((r: any, index: number) =>
              r?.isChildren === true && r?.parentGroupValue === groupValue ? index : -1
            )
            .filter((index: number) => index !== -1);

          const lastVisibleChildIndex = visibleChildIndexes.length
            ? visibleChildIndexes[visibleChildIndexes.length - 1]
            : parentIndex;

          // Remove the current "Show More" row
          newRows.splice(showMoreIndex, 1);

          // If the Show More row was before the last child index, adjust the index after removal
          const adjustedLastVisibleChildIndex =
            showMoreIndex < lastVisibleChildIndex
              ? lastVisibleChildIndex - 1
              : lastVisibleChildIndex;

          // Insert new children after the last visible child
          const insertPosition = adjustedLastVisibleChildIndex + 1;
          newRows.splice(insertPosition, 0, ...childrenWithFlag);

          // Insert new "Show More" row if there are more children
          if (hasMoreChildren) {
            const newShowMoreRow = {
              id: `show-more-${groupValue}-${newShowCount}`,
              isShowMoreRow: true,
              parentRow: {
                ...currentParent,
                totalChildrenCount: totalCount
              },
              currentShowCount: newShowCount,
              totalChildrenCount: totalCount,
              [actualGroupByValue]: groupValue,
              groupBy: actualGroupByValue,
              isLoading: false
            };
            newRows.splice(insertPosition + uniqueChildren.length, 0, newShowMoreRow);
          }

          // Update parent's data - Merge the children array
          const updatedChildren = [...(currentParent.children || [])];
          uniqueChildren.forEach((newChild: any) => {
            const exists = updatedChildren.some((existingChild: any) =>
              (existingChild.sys_id && newChild.sys_id && existingChild.sys_id === newChild.sys_id) ||
              (existingChild.number && newChild.number && existingChild.number === newChild.number)
            );
            if (!exists) {
              updatedChildren.push(newChild);
            }
          });

          // Update parent state
          newRows[parentIndex] = {
            ...currentParent,
            showChildrenCount: newShowCount,
            children: updatedChildren,
            isExpanded: true,
            totalChildrenCount: totalCount // Ensure parent has correct total
          };

          return newRows;
        });
      }
    } catch (error) {
      console.error('Error loading more children:', error);
    } finally {
      setLoadingGroupId(null);
    }
  }, [loadMoreChildren, groupBy, getGroupValue, setLoadingGroupId]);

    const handleManageSop = () => {
      setIsManageSopOpen(true);
    };

  // In the useEffect that sets groupedData from tableData
  useEffect(() => {
    if (alerts && alerts.length > 0) {
      const isAlreadyGrouped = groupBy && groupBy !== 'none' && (alerts[0]?.isParent === true || Array.isArray(alerts[0]?.children));

      if (isAlreadyGrouped) {
        const resetGroupedData = alerts.map((group: any) => {
          const groupValue = getGroupValue(group, groupBy);
          const countField = `${groupBy}Count`;
          const totalChildrenCount = getGroupCount(group, groupBy);

          return {
            ...group,
            [groupBy]: groupValue,
            [countField]: group?.[countField] ?? totalChildrenCount,
            isParent: true,
            isExpanded: false,
            showChildrenCount: 50,
            // Store the actual total children count from the API response if available
            // This should come from the API response in the group object
            totalChildrenCount,
            children: Array.isArray(group?.children) ? group.children : [],
          };
        });
        setIsGrouped(true);
        setGroupedData(resetGroupedData);
      } else {
        setIsGrouped(false);
        setGroupedData(alerts);
      }
    } else {
      setGroupedData([]);
      setIsGrouped(false);
    }
  }, [alerts, groupBy, getGroupValue, getGroupCount, setIsGrouped]);

  const transformSummaryData = (rawSummary: any) => {
    if (!rawSummary) return null;
    if (rawSummary.CRITICAL && rawSummary.MAJOR && rawSummary.MINOR && rawSummary.WARNING && rawSummary.INFORMATION && rawSummary.CLOSED) {
      return {
        criticalAlerts: rawSummary.CRITICAL,
        majorAlerts: rawSummary.MAJOR,
        minorAlerts: rawSummary.MINOR,
        warningAlerts: rawSummary.WARNING,
        information: rawSummary.INFORMATION,
        closedAlerts: rawSummary.CLOSED,
        ewsCriticalAlerts: rawSummary.EWSMEDIUM,
      };
    }
    return null;
  };

  const transformedSummary = summary ? transformSummaryData(summary) : null;

  const duplicateCounts: Record<string, number> = {
    criticalAlerts: summary?.CRITICAL?.duplicateCount ?? 0,
    majorAlerts: summary?.MAJOR?.duplicateCount ?? 0,
    minorAlerts: summary?.MINOR?.duplicateCount ?? 0,
    warningAlerts: summary?.WARNING?.duplicateCount ?? 0,
    closedAlerts: summary?.CLOSED?.duplicateCount ?? 0,
  };

  const allDiff = summary?.ALL?.difference ?? null;
  const totalSubtitle = allDiff !== null ? `${Math.abs(allDiff)} from last hour` : undefined;
    const totalTrend: 'up' | 'down' | 'neutral' =
    summary?.ALL?.trend === 'INCREASED'
      ? 'up'
      : summary?.ALL?.trend === 'DECREASED'
      ? 'down'
      : 'neutral';
  const getTimeRangeLabel = () => {
  const sessionRange = sessionStorage.getItem('unifiedAlertsDateRangeType');
  const effectiveRange = sessionRange || appliedFilters.timeRange;

  if (effectiveRange === 'custom') {
    const start = appliedFilters.startDate || sessionStorage.getItem('unifiedAlertsStartDate');
    const end = appliedFilters.endDate || sessionStorage.getItem('unifiedAlertsEndDate');

    if (start && end) {
      const startDate = moment(start).tz(TIMEZONE_MAP.CST).format('MM-DD-YYYY');
      const endDate = moment(end).tz(TIMEZONE_MAP.CST).format('MM-DD-YYYY');
      return `${startDate} – ${endDate}`;
    }

    return 'Custom Range';
  }

  const optionLabel = TIME_RANGE_OPTIONS.find(o => o.value === effectiveRange)?.label;

  if (optionLabel) return optionLabel;

  const labelMap: Record<string, string> = {
    '5m':   'Last 5 Minutes',
    '10m':  'Last 10 Minutes',
    '15m':  'Last 15 Minutes',
    '30m':  'Last 30 Minutes',
    '1h':   'Last 1 Hour',
    'today':'Today',
    '24h':  'Last 24 Hours',
    '7d':   'Last 7 Days',
    '30d':  'Last 30 Days',
    '90d':  'Last 90 Days',
  };

  return labelMap[effectiveRange] || '';
};

const timeRange = getTimeRangeLabel();

  const handleSourceSliceClick = (source: string) => {
    setActiveContent("table");
    handleApplyFilters({
      ...appliedFilters,
      sources: [{ label: source, value: source }]
    });
  };

  const handleOpenPieSliceClick = (severity: string) => {
    setActiveTile(severity)
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('ignoreDate', 'true');
      return params;
    });
    setScreen({
      type: 'table',
    });
  };

  const handleBack = () => {

  setScreen({
    type: 'overview',
  });
};

  const chartContent = [
    <TrendBySector key="sector" series={trendSector?.series || []} stats={{ ...(trendSector?.stats || {}), totalSubtitle, totalTrend }} isLoading={isChartsLoading} />,
    <TrendByProgram key="program" series={trendProgram?.series || []} stats={{ ...(trendProgram?.stats || {}), totalSubtitle, totalTrend }} isLoading={isChartsLoading} />,
    <SourcesPieChart key="sources" data={sources} isLoading={isChartsLoading} onSliceClick={handleSourceSliceClick} />,
    <MostImpactedApp key="impacted" data={mostImpactedApps} isLoading={isChartsLoading} />,
    <AlertsOverview
      key="severityPie"
      data={severityData}
      isLoading={isChartsLoading}
      onSliceClick={handleOpenPieSliceClick}
    />
  ];

  const selectedTile = TILES_CONFIG.find(t => t.key === activeTile);
  const isScreenshotOnlyChartSelected = activeContent === 'table';

  const handleScreenCapture = async (captureTargetId: string) => {
  try {
    setScreenCapturing(true)

    const element = document.getElementById(captureTargetId)

    if (!element) {
      console.warn(`Capture target not found: ${captureTargetId}`)
      return
    }

    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      scrollY: -window.scrollY,
      ignoreElements: (el: any) =>
        ['exclude-camera', 'exclude-export'].includes(el.id),
    })

    const image = canvas.toDataURL('image/png')

    setPreviewImage(image)
    setOpenPreview(true)
  } catch (err) {
    console.error('Screenshot failed', err)
  } finally {
    setScreenCapturing(false)
  }
}

  const handleDownloadPDF = async () => {
      if (isPdfGenerating) return;

      setIsPdfGenerating(true);

      try {
        const pdfData = await fetchPdfData();

        // ============================================================
        // Safety check
        // ============================================================

        if (!Array.isArray(pdfData) || pdfData.length === 0) {
          console.warn('No PDF data available');
          return;
        }

        // ============================================================
        // Detect which type of data we received
        //
        // EWS response contains:
        //   row.ruleConfig
        //
        // Normal Alerts response does not.
        // ============================================================

        const isEwsData = pdfData.some(
          (row: any) => row?.ruleConfig
        );


        // ============================================================
        // EWS FLOW
        // ============================================================

        if (isEwsData) {

          // ----------------------------------------------------------
          // EWS PDF column definitions
          // ----------------------------------------------------------

          const ewsPdfColumns: PdfColumn[] = [
            {
              key: 'ruleName',
              label: 'RULE NAME',
              weight: 25,
              formatter: (row: any) =>
                row?.ruleName ?? '--'
            },

            {
              key: 'domain',
              label: 'DOMAIN',
              weight: 20,
              formatter: (row: any) =>
                row?.domain ?? '--'
            },

            {
              key: 'component',
              label: 'COMPONENT',
              weight: 20,
              formatter: (row: any) =>
                row?.component ?? '--'
            },

            {
              key: 'ewsSignalName',
              label: 'EWS SIGNAL NAME',
              weight: 30,
              formatter: (row: any) =>
                row?.ewsSignalName ?? '--'
            },

            {
              key: 'riskLevel',
              label: 'RISK LEVEL',
              weight: 18,
              formatter: (row: any) =>
                row?.riskLevel ?? '--',
              isStatus: true
            },

            {
              key: 'dataSource',
              label: 'DATA SOURCE',
              weight: 25,
              formatter: (row: any) =>
                row?.dataSource ?? '--'
            },

            {
              key: 'layerTool',
              label: 'LAYER / TOOL',
              weight: 20,
              formatter: (row: any) =>
                row?.layerTool ?? '--'
            },

            {
              key: 'handler',
              label: 'HANDLER',
              weight: 20,
              formatter: (row: any) =>
                row?.handler ?? '--'
            },

            {
              key: 'handlerType',
              label: 'HANDLER TYPE',
              weight: 20,
              formatter: (row: any) =>
                row?.handlerType ?? '--'
            },

            {
              key: 'handlerStatus',
              label: 'HANDLER STATUS',
              weight: 20,
              formatter: (row: any) =>
                row?.handlerStatus ?? '--',
              isStatus: true
            },

            {
              key: 'durationMs',
              label: 'DURATION (s)',
              weight: 20,
              formatter: (row: any) => {
                const value = row?.durationMs;

                if (
                  value === undefined ||
                  value === null ||
                  value === 'N/A'
                ) {
                  return '--';
                }

                const numericValue = Number(value);

                if (Number.isNaN(numericValue)) {
                  return String(value);
                }

                return `${(numericValue / 1000).toFixed(2)}s`;
              }
            },

            {
              key: 'avgLastTenDuration',
              label: 'AVG LAST 10 DURATION (s)',
              weight: 25,
              formatter: (row: any) => {
                const value =
                  row?.avgLastTenDuration;

                if (
                  value === undefined ||
                  value === null ||
                  value === 'N/A'
                ) {
                  return '--';
                }

                const numericValue = Number(value);

                if (Number.isNaN(numericValue)) {
                  return String(value);
                }

                return `${(numericValue / 1000).toFixed(2)}s`;
              }
            },

            {
              key: 'resourceName',
              label: 'RESOURCE NAME',
              weight: 25,
              formatter: (row: any) =>
                row?.resourceName ?? '--'
            },

            {
              key: 'resourceId',
              label: 'RESOURCE ID',
              weight: 30,
              formatter: (row: any) =>
                row?.resourceId ?? '--'
            },

            {
              key: 'ruleId',
              label: 'RULE ID',
              weight: 30,
              formatter: (row: any) =>
                row?.ruleId ?? '--'
            },

            {
              key: 'createdAt',
              label: 'CREATED DATE',
              weight: 25,
              formatter: (row: any) => {
                const value = row?.createdAt;

                if (!value || value === 'N/A') {
                  return '--';
                }

                const date = new Date(value);

                if (Number.isNaN(date.getTime())) {
                  return String(value);
                }

                return date.toLocaleString('en-US');
              }
            }
          ];


          // ----------------------------------------------------------
          // Convert raw EWS response into the same flat structure
          // used by your EWS table.
          // ----------------------------------------------------------

          const ewsPdfRows = pdfData.map(
            (item: any) =>
              flattenAlertItem(item)
          );


          // ----------------------------------------------------------
          // Generate EWS PDF
          // ----------------------------------------------------------

          await generateDashboardPdf({
            columns: ewsPdfColumns,

            allData: ewsPdfRows,

            dashboardTitle:
              'EWS Rule Results',

            tableTitle:
              selectedTile?.label ??
              activeTile,

            totalElements:
              ewsPdfRows.length,

            topKpiElementId:
              'ua-top-tabs',

            fileNamePrefix:
              `EWSRuleResults_${activeTile}`,
          });


          // ----------------------------------------------------------
          // IMPORTANT:
          //
          // We are done with EWS flow.
          // Don't execute normal Alerts PDF logic.
          // ----------------------------------------------------------

          return;
        }


        // ============================================================
        // NORMAL UNIFIED ALERTS FLOW
        // ============================================================

        // ── Date/time formatter ──────────────────────────────────────

        const fmtDateTime = (field: string) =>
          (row: any): string => {

            const value =
              row?.[field];

            if (!value) {
              return '--';
            }

            const date =
              new Date(value);

            return Number.isNaN(
              date.getTime()
            )
              ? String(value)
              : date.toLocaleString('en-US');
          };


        // ── Severity formatter ───────────────────────────────────────

        const fmtSeverity = (row: any): string => {

          const value = String(
            row?.severity ??
            row?.alert_severity ??
            ''
          ).toUpperCase();

          if (value === 'OK') {
            return 'INFORMATION';
          }

          return value || '--';
        };


        // ── AGE formatter ───────────────────────────────────────────

        const fmtAge = (row: any): string => {

          const startTime =
            row?.initial_event_time
              ? new Date(
                  row.initial_event_time
                ).getTime()
              : NaN;

          const endTime =
            Date.now();

          const diffMs =
            endTime - startTime;

          if (
            Number.isNaN(diffMs) ||
            diffMs < 0
          ) {
            return '-';
          }

          const seconds =
            Math.floor(
              diffMs / 1000
            );

          const minutes =
            Math.floor(
              seconds / 60
            );

          const hours =
            Math.floor(
              minutes / 60
            );

          const days =
            Math.floor(
              hours / 24
            );

          if (days > 0) {
            return `${days}d ${hours % 24}h`;
          }

          if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
          }

          if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
          }

          return `${seconds}s`;
        };


        // ── All possible normal PDF columns ─────────────────────────

        const allPdfColumnsMap: Record<
          string,
          PdfColumn
        > = {

          number: {
            key: 'number',
            label: 'ALERT ID',
            weight: 40,
            formatter: r =>
              r?.number || '--'
          },

          description: {
            key: 'description',
            label: 'DESCRIPTION',
            weight: 30,
            formatter: r =>
              String(
                r?.description ?? '--'
              )
                .replace(
                  /[\r\n]+/g,
                  ' '
                )
                .slice(0, 70)
          },

          source: {
            key: 'source',
            label: 'SOURCE',
            weight: 25,
            formatter: r =>
              r?.source || '--'
          },

          mappedSeverity: {
            key: 'severity',
            label: 'SEVERITY',
            weight: 20,
            formatter: fmtSeverity,
            isStatus: true
          },

          severity: {
            key: 'severity',
            label: 'SEVERITY',
            weight: 20,
            formatter: fmtSeverity,
            isStatus: true
          },

          sn_priority_group: {
            key: 'sn_priority_group',
            label: 'PRIORITY',
            weight: 20,
            formatter: r =>
              r?.sn_priority_group || '--',
            isStatus: true
          },

          state: {
            key: 'state',
            label: 'STATUS',
            weight: 25,
            formatter: r =>
              r?.state || '--'
          },

          age: {
            key: 'age',
            label: 'AGE',
            weight: 25,
            formatter: fmtAge
          },

          incident: {
            key: 'incident',
            label: 'INCIDENT',
            weight: 13,
            formatter: r =>
              r?.inc_sys_id
                ? r?.incident || '--'
                : 'NA'
          },

          assignment_group: {
            key: 'assignment_group',
            label: 'ASSIGNMENT GROUP',
            weight: 25,
            formatter: r =>
              r?.assignment_group || '--'
          },

          last_event_time: {
            key: 'last_event_time',
            label: 'LAST EVENT TIME',
            weight: 30,
            formatter:
              fmtDateTime(
                'last_event_time'
              )
          },

          comments: {
            key: 'comments',
            label: 'COMMENTS',
            weight: 20,
            formatter: r =>
              r?.comments || '--'
          },

          category: {
            key: 'category',
            label: 'CATEGORY',
            weight: 20,
            formatter: r =>
              r?.category || '--'
          },

          domain: {
            key: 'domain',
            label: 'DOMAIN',
            weight: 20,
            formatter: r =>
              r?.domain || '--'
          },

          classification: {
            key: 'classification',
            label: 'CLASSIFICATION',
            weight: 16,
            formatter: r =>
              r?.classification || '--'
          },

          cmdb_ci: {
            key: 'cmdb_ci',
            label: 'CI',
            weight: 13,
            formatter: r =>
              r?.cmdb_ci || '--'
          },

          sys_created_by: {
            key: 'sys_created_by',
            label: 'CREATED BY',
            weight: 15,
            formatter: r =>
              r?.sys_created_by || '--'
          },

          sys_updated_by: {
            key: 'sys_updated_by',
            label: 'UPDATED BY',
            weight: 15,
            formatter: r =>
              r?.sys_updated_by || '--'
          },

          sector: {
            key: 'sector',
            label: 'SECTOR',
            weight: 20,
            formatter: r =>
              r?.sector || '--'
          },

          application: {
            key: 'application',
            label: 'APPLICATION',
            weight: 20,
            formatter: r =>
              r?.application || '--'
          },

          businessUnit: {
            key: 'businessUnit',
            label: 'BUSINESS UNIT',
            weight: 20,
            formatter: r =>
              r?.businessUnit || '--'
          },

          capability: {
            key: 'capability',
            label: 'CAPABILITY',
            weight: 20,
            formatter: r =>
              r?.capability || '--'
          },

          department: {
            key: 'department',
            label: 'DEPARTMENT',
            weight: 20,
            formatter: r =>
              r?.department || '--'
          },

          program: {
            key: 'program',
            label: 'PROGRAM',
            weight: 20,
            formatter: r =>
              r?.program || '--'
          }
        };


        // ── Normal table column mapping ─────────────────────────────

        const tableColNameToKey: Record<
          string,
          string
        > = {

          'ALERT ID':
            'number',

          'DESCRIPTION':
            'description',

          'SOURCE':
            'source',

          'SEVERITY':
            'mappedSeverity',

          'PRIORITY':
            'sn_priority_group',

          'STATUS':
            'state',

          'AGE':
            'age',

          'INCIDENT':
            'incident',

          'ASSIGNMENT GROUP':
            'assignment_group',

          'LAST EVENT TIME':
            'last_event_time',

          'COMMENTS':
            'comments',

          'CATEGORY':
            'category',

          'DOMAIN':
            'domain',

          'CLASSIFICATION':
            'classification',

          'CI':
            'cmdb_ci',

          'CREATED BY':
            'sys_created_by',

          'UPDATED BY':
            'sys_updated_by',

          'SECTOR':
            'sector',

          'APPLICATION':
            'application',

          'BUSINESS UNIT':
            'businessUnit',

          'CAPABILITY':
            'capability',

          'DEPARTMENT':
            'department',

          'PROGRAM':
            'program'
        };


        // ── User column preferences ─────────────────────────────────

        const storedPref =
          localStorage.getItem(
            'UnifiedAlertsSnow_user_col_pref'
          );

        let preferredColumnNames:
          string[] | null = null;


        if (
          storedPref &&
          storedPref !== 'undefined'
        ) {
          try {
            preferredColumnNames =
              JSON.parse(
                storedPref
              );
          } catch {
            preferredColumnNames = null;
          }
        }


        // ── Default columns ─────────────────────────────────────────

        const defaultPdfColumns = [

          'ALERT ID',
          'DESCRIPTION',
          'SOURCE',
          'SEVERITY',
          'PRIORITY',
          'STATUS',
          'AGE',
          'INCIDENT',
          'ASSIGNMENT GROUP',
          'LAST EVENT TIME',
          'COMMENTS',
          'CATEGORY',
          'DOMAIN',
          'CLASSIFICATION',
          'CI',
          'CREATED BY',
          'UPDATED BY',
          'SECTOR',
          'APPLICATION',
          'BUSINESS UNIT',
          'CAPABILITY',
          'DEPARTMENT',
          'PROGRAM'
        ];


        const columnNames =
          preferredColumnNames &&
          Array.isArray(
            preferredColumnNames
          ) &&
          preferredColumnNames.length > 0

            ? preferredColumnNames.filter(
                name =>
                  name !== 'ACTIVITY' &&
                  name !== 'ACTIONS'
              )

            : defaultPdfColumns;


        // ── Build normal PDF columns ────────────────────────────────

        const pdfColumns: PdfColumn[] =
          columnNames

            .map(name => {

              const dataKey =
                tableColNameToKey[
                  name
                ];

              if (!dataKey) {
                return undefined;
              }

              const pdfCol =
                allPdfColumnsMap[
                  dataKey
                ];

              if (!pdfCol) {
                return undefined;
              }

              return {
                ...pdfCol,
                label: name
              };
            })

            .filter(
              (
                col
              ): col is PdfColumn =>
                col !== undefined
            );


        // ============================================================
        // Generate NORMAL Unified Alerts PDF
        // ============================================================

        await generateDashboardPdf({

          columns:
            pdfColumns,

          allData:
            pdfData,

          dashboardTitle:
            'Alerts Summary Dashboard',

          tableTitle:
            selectedTile?.label ??
            activeTile,

          totalElements:
            totalAlerts,

          topKpiElementId:
            'ua-top-tabs',

          fileNamePrefix:
            `AlertsDashboard_${activeTile}`
        });


      } catch (err) {

        console.error(
          'PDF generation failed:',
          err
        );

      } finally {

        setIsPdfGenerating(false);
      }
    };

  useEffect(() => {
        setRightContextContent(
          <Box display="flex" alignItems="center" gap={2}>
              <CustomSwitch
                checked={alertCountView === 'unique'}
                onChange={(checked) => {
                  setAlertCountView(checked ? 'unique' : 'overall');
                }}
                startLabel="Alert overall counts"
                endLabel="Alert unique counts"
                size="small"
                minHeight={0}
              />
            <AdvancedFilterPanel
              open={isFilterDrawerOpen}
              onClose={() => setIsFilterDrawerOpen(false)}
              onApply={handleApplyFilters}
              onSaveDefault={handleSaveDefaultFilters}
              onResetDefault={handleResetDefaultFilters}
              currentFilters={appliedFilters}
              filterOptions={filterOptions}
              filterRelationshipRecords={filterRelationshipRecords}
              lockedStatus={activeTile === 'Closed' ? 'Closed' : undefined}
              defaultStatus={activeTile === 'Closed' ? undefined : OPEN_TILE_STATUSES}
            />
          </Box>,
        );
    
        return () => {
          setRightContextContent(null);
        };
      }, [appliedFilters, filterOptions, activeTile, alertCountView]);

    const alertsContextValue = useMemo(
          () => ({
            groupedData,
            totalAlerts,
            page,
            pageSize,
            setPage,
            setPageSize,
            filterOptions,
            sortingInfo,
            setSortingInfo,
            isLoading,
            selectedTile,
            groupBy,
            setGroupBy,
            groupByList,
            includeGroupByMenuColumns,
            handleGroupToggle,
            handleShowMoreClick,
            loadingGroupId,
            isDataOpsAdmin,
            handleManageSop,
            alertCountView,
            columnFilters,
            handleColumnFilterChange,
            getColumnFilterValues,
          }),
      [
        groupedData,
        totalAlerts,
        page,
        pageSize,
        filterOptions,
        sortingInfo,
        isLoading,
        selectedTile,
        groupBy,
        groupByList,
        includeGroupByMenuColumns,
        loadingGroupId,
        isDataOpsAdmin,
        handleGroupToggle,
        handleShowMoreClick,
        handleManageSop,
        alertCountView,
        columnFilters,
        handleColumnFilterChange,
        getColumnFilterValues,
      ]
    );

  return (
    <AlertsProvider
      value={alertsContextValue}
    >

    <PageWrapper sx={{ minHeight: 'calc(100vh - 120px)', backgroundColor: p.backgroundLight.sideNavBgHover, position: 'relative' }}>
      
     {screen.type === 'overview' ? (
          <Box id="ua-main" sx={{ pt: 1, position: 'relative', backgroundColor: p.backgroundLight.sideNavBgHover }}>
              <Box id="ua-capture-area">
                    <Box id="ua-top-tabs" sx={{ display: 'flex', alignItems: 'flex-end', gap: 1}}>
                      <Grid container spacing={1} sx={{ flex: 1 }}>
                        <Grid item xs={12}>
                          <SeverityTiles
                            summary={transformedSummary}
                            activeTile={activeContent === 'chart' ? '' : activeTile}
                            onTileClick={(key) => { 
                              handleTileClick(key); 
                              setActiveContent('table'); 
                              setSearchParams((prev) => {
                                const params = new URLSearchParams(prev);
                                params.delete('ignoreDate');
                                return params;
                              });
                            }}

                            isLoading={summaryLoading}
                            timePeriod={timeRange}
                            duplicateCounts={duplicateCounts}
                            alertCountView={alertCountView}
                            sx={{
                              width: '100% !important',
                              flexWrap: 'wrap',
                              gap: '8px',
                              '& > div': {
                                flex: '1 1 calc(25% - 8px) !important',
                                maxWidth: 'calc(25% - 8px) !important',
                                boxSizing: 'border-box'
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                      <CaptureExportControls
                        onScreenshot={() => handleScreenCapture('ua-capture-area')}
                        screenCapturing={screenCapturing}
                        onExport={handleExportClick}
                        isExporting={isExporting}
                        showExport={isScreenshotOnlyChartSelected}
                        isVisible={true}
                        type="snow"
                        exportTooltip="Max 200K records will be exported at a Time."
                      />
                    </Box>

                    <Box id="ua-tab-content" sx={{ backgroundColor: p.white.main, p: 3, pt: activeContent === 'chart' ? 5 : 3, position: 'relative', minHeight: '300px' }}>
                        <Box
                        sx={{
                          position: 'absolute',
                          bottom: '100%',
                          right: 0,
                          display: activeContent === 'table' ? 'flex' : 'none',
                          alignItems: 'center',
                          gap: 1,
                          borderRadius: 1,
                        }}
                      >
                      </Box>
                      {activeContent === 'table' ? (
                        <AlertsTableWrapper idleFilterIcon={IdleFilterIconBlue} />
                      ) : (
                        chartContent[activeBottomTab]
                      )}
                    </Box>
              </Box>

              <ManageSopDialog
                open={isManageSopOpen}
                onClose={() => setIsManageSopOpen(false)}
                type="EWS"
                activeTile={activeTile}
                entityConfig={alertSopEntityConfig}
              />

              <Box id="ua-bottom-tabs" sx={{ display: 'flex', gap: 0.5, width: '66.67%' }}>
                {CHART_CARDS.map((card) => {
                  const isActive = activeContent === 'chart' && activeBottomTab === card.key;
                  const isHovered = hoveredBottomTab === card.key;
                  const { Icon } = card;
                  const iconColor = isActive || isHovered ? p.primary.main : p.white.main;
                  return (
                    <Box
                      key={card.key}
                      onClick={() => {
                        setActiveBottomTab(card.key);
                        setActiveContent('chart');

                        setSearchParams((prev) => {
                          const params = new URLSearchParams(prev);

                          if (card.key === 4) {
                            params.set('ignoreDate', 'true');
                          } else {
                            params.delete('ignoreDate');
                          }

                          return params;
                        });
                      }}
                      onMouseEnter={() => setHoveredBottomTab(card.key)}
                      onMouseLeave={() => setHoveredBottomTab(null)}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        gap: 0,
                        py: 2.5,
                        px: 2,
                        cursor: 'pointer',
                        backgroundColor: isActive || isHovered ? p.white.main : p.primary.main,
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <Box sx={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                        <Icon color={iconColor} />
                      </Box>
                      <Typography variant="body1" sx={{ color: iconColor, fontWeight: 500, textAlign: 'right', lineHeight: 1.2 }}>
                        {card.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: iconColor, textAlign: 'right' }}>
                        {timeRange}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
          </Box>
      ) : (
        <AlertsTablePage
          onBack={handleBack}
          onScreenshot={() =>
            handleScreenCapture('ua-table-capture-area')
          }
          screenCapturing={screenCapturing}
          onExport={handleExportClick}
          isExporting={isExporting}
        />
      )}

      <ExportLimitWarningDialog
        isOpen={isExportWarningOpen}
        limit={filterOptions?.exportLimit || 200000}
        handleClose={() => setIsExportWarningOpen(false)}
        handleConfirm={handleExport}
      />

      <ImagePreview
        open={openPreview}
        image={previewImage}
        fileName={`screenshot_${selectedTile?.label}_${formattedDate}`}
        onClose={() => setOpenPreview(false)}
        title={'Alerts Summary Dashboard'}
        hasDownloadPDFButton
        handleDownloadPDF={handleDownloadPDF}
        isPdfGenerating={isPdfGenerating}
      />
    </PageWrapper>
  </AlertsProvider>
    
  );
};

export default SnowUnifiedAlertsDashboard;